<template>
  <v-container class="modern-master-view pb-12">
    <!-- Global Header -->
    <v-row v-if="!stand_alone" class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-history</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Bitácora de Consumo</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Registro detallado de materiales por OT</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <export-consume-o-t-bitacora 
        :filter="filter" 
        :options="options" 
        :disabled="loading"
        class="shadow-sm"
      />
    </v-row>

    <!-- Filters Section -->
    <v-card v-if="!stand_alone" outlined class="modern-card shadow-sm pa-4 mb-8">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            clearable 
            type="date" 
            class="modern-input"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            clearable 
            outlined 
            type="date" 
            class="modern-input"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1">Grupo de Artículos</div>
          <v-select
            v-model="filter.family"
            :items="families"
            :loading="familiesLoading"
            item-text="name"
            item-value="id"
            placeholder="Todos los grupos"
            outlined
            dense
            clearable
            hide-details
            class="modern-input"
            prepend-inner-icon="fas fa-filter"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="2" class="d-flex align-end pb-1">
          <v-btn block color="primary" outlined class="font-weight-black h-40" @click="fetch" :loading="loading">
            <v-icon left small>fas fa-sync</v-icon> Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table Card -->
    <v-card outlined :class="stand_alone ? 'transparent elevation-0' : 'modern-card shadow-sm overflow-hidden'">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 30, 40, 50, 100],
        }"
        @update:options="fetch"
      >
        <template v-slot:top v-if="!stand_alone">
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Movimientos de Consumo</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              placeholder="Buscar por artículo, cliente o transacción..."
              prepend-inner-icon="fas fa-search"
              outlined
              dense
              hide-details
              clearable
              @keyup="searchTimeOut"
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <!-- Column Templates -->
        <template v-slot:item.created_at="{ item }">
          <div class="body-2 grey--text text--darken-2 font-weight-medium">
            {{ item.created_at | moment("DD/MM/YYYY") }}
          </div>
        </template>

        <template v-slot:item.transaction="{ item }">
          <v-chip x-small label outlined color="grey darken-1" class="font-weight-bold">
            {{ item.transaction }}
          </v-chip>
        </template>

        <template v-slot:item.workorder="{ item }">
          <show-work-order :work_order_id="item.workorder.id" v-if="item.workorder">
            <div class="d-flex flex-column py-1">
              <span class="caption grey--text font-weight-bold uppercase lh-1 mb-1">{{ item.workorder.customer_name }}</span>
              <span class="body-2 font-weight-black primary--text lh-1">OT #{{ item.workorder.id }}</span>
            </div>
          </show-work-order>
        </template>

        <template v-slot:item.article__sku="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="caption primary--text font-weight-black uppercase lh-1 mb-1">[{{ item.article.sku }}]</span>
            <span class="caption grey--text text--darken-2 font-weight-medium lh-1">{{ item.article.name }}</span>
          </div>
        </template>

        <template v-slot:item.articlePrice="{ item }">
          <span class="body-2 font-weight-medium grey--text text--darken-2">
            {{ item.articlePrice | moneda }}
          </span>
        </template>

        <template v-slot:item.convertedPrice="{ item }">
          <div class="d-flex flex-column align-end">
            <span class="caption grey--text font-weight-bold lh-1 mb-1" title="Factor de consumo">
              x{{ item.unitConsume_factor | number('0.0000') }}
            </span>
            <span class="body-2 font-weight-black primary--text lh-1">
              {{ item.convertedPrice | moneda }}
            </span>
          </div>
        </template>

        <template v-slot:item.qty="{ item }">
          <v-chip small color="primary" label dark class="font-weight-black px-2">
            {{ item.qty | number('0,0') }}
          </v-chip>
        </template>

        <template v-slot:item.total="{ item }">
          <span class="body-2 font-weight-black primary--text">
            {{ item.total | moneda }}
          </span>
        </template>

        <template v-slot:item.created_by__first_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="grey lighten-3" class="mr-2">
              <v-icon x-small color="grey darken-1">fas fa-user</v-icon>
            </v-avatar>
            <span class="caption grey--text text--darken-2 font-weight-bold">{{ item.created_by_full_name }}</span>
          </div>
        </template>

      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fetchFamilies } from '@/api/stocks'
import { fetchConsumtionBitacoraByOT } from '@/api/production'
import ExportConsumeOTBitacora from '@/components/reports/ExportConsumeOTBitacora'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'ConsumeOTBitacora',
  components: { ExportConsumeOTBitacora },
  props: {
    workorder_id: {
      type: Number,
      default: 0
    },
    stand_alone: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before: '',
        family: '',
      },
      options: {},
      totalItems: 0,
      search: '',
      items: [],
      families: [],
      familiesLoading: false,
      timer: null,
      headers: [
        { text: 'Creada', align: 'start', value: 'created_at', class: 'table-header', width: '8%' },
        { text: 'Transac.', value: 'transaction', class: 'table-header', align: 'center', width: '8%' },
        { text: 'Cliente | OT', align: 'start', value: 'workorder', class: 'table-header', width: '20%' },
        { text: 'Artículo', align: 'start', value: 'article__sku', class: 'table-header', width: '20%' },
        { text: 'Unid. Compra', align: 'start', value: 'unit', class: 'table-header', sortable: false },
        { text: 'Precio Prom.', align: 'end', value: 'articlePrice', class: 'table-header', sortable: false },
        { text: 'Unid. Consumo', align: 'start', value: 'unitConsume', class: 'table-header', sortable: false },
        { text: 'Precio Conv.', align: 'end', value: 'convertedPrice', class: 'table-header', sortable: false },
        { text: 'Cant.', align: 'end', value: 'qty', class: 'table-header', sortable: false },
        { text: 'Total', align: 'end', value: 'total', class: 'table-header', sortable: true },
        { text: 'Usuario', align: 'start', value: 'created_by__first_name', class: 'table-header', sortable: true },
      ]
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      let created_before = ''
      let created_after = ''
      
      if (this.filter.created_after) 
        created_after = moment(this.filter.created_after).utc().format('yyyy-MM-DD HH:mm')
      if (this.filter.created_before) 
        created_before = moment(this.filter.created_before).utc().add(1439, 'minutes').format('yyyy-MM-DD HH:mm')

      if (!this.filter.family) this.filter.family = ''
      
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let query = ''
      
      if (this.stand_alone && this.workorder_id) {
        query = `ordering=${sortDesc && sortDesc[0] ? sortBy[0] : '-' + (sortBy ? sortBy[0] : 'id')}&page=${page || 1}&page_size=${itemsPerPage || 20}&workorder=${this.workorder_id}`
      } else {
        query = `ordering=${sortDesc && sortDesc[0] ? sortBy[0] : '-' + (sortBy ? sortBy[0] : 'created_at')}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search}&created_after=${created_after}&created_before=${created_before}&article__family=${this.filter.family}`
      }

      await fetchConsumtionBitacoraByOT(query)
        .then((data) => {
          this.totalItems = data.count
          this.items = data.results
        })
        .catch((err) => notifications.showError(err))
        .finally(() => this.loading = false)
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.fetch()
      }, 800);
    },
  },
  async mounted() {
    this.familiesLoading = true
    this.families = await fetchFamilies()
    this.familiesLoading = false
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1 !important; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.h-40 { height: 40px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-search-field { width: 320px; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; background-color: white !important; }
.modern-search-field >>> .v-input__control { box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; border-bottom: 2px solid #eef2f5 !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; }

.fill-width { width: 100%; }
</style>
